// lib
import { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'

// components
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { MultiStateMultiSelect } from 'primereact/multistatemultiselect'

import MultiSelectHeader from 'primereact-raytm-theme/MultiSelectHeader'
import MultiSelectFooter from 'primereact-raytm-theme/MultiSelectFooter'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

const FiltersRowCustomVariable = ({ filter, removable = true, filterValues, filterOptions }) => {
  const {
    features,
    state,
    dropdownsRef,
    onBasicFilterChange,
    onResetBasicFilter,
    onShowFilterDropdown,
    onHideFilterDropdown,
    onVariableRemove,
    getVariableError,
    getVariableProcessing
  } = useContext(DataContext)

  const { mode } = state

  const error = getVariableError(filter)
  const processing = getVariableProcessing(filter)

  const [values, setValues] = useState(null !== filterValues ? JSON.parse(filterValues) : filterValues)
  const [options, setOptions] = useState(filterOptions)

  const filterRef = useRef(null)

  const dndListItemRef = useRef()

  const filterDisabled = useMemo(
    () => features.filters.disabled || filter.disabled,
    [features.filters.disabled, filter.disabled]
  )
  const filterSelected = useMemo(
    () => ('advanced' == mode || filterDisabled ? false : null !== values && 0 < values.length),
    [mode, values, filterDisabled]
  )

  const onFilterChange = useCallback(
    event => {
      switch (filter.type) {
        case 'dropdown': {
          const values = [event.value]

          onBasicFilterChange(filter, values)
          break
        }
        case 'multiselect': {
          const values = event.value
          values.sort((a, b) => options.findIndex(v => v.value == a) - options.findIndex(v => v.value == b))

          onBasicFilterChange(filter, values)
          break
        }
        case 'multistatemultiselect': {
          const values = event.value
          values.sort((a, b) => options.findIndex(v => v.value == a.value) - options.findIndex(v => v.value == b.value))

          onBasicFilterChange(filter, values)
          break
        }
        default: {
          break
        }
      }
    },
    [filter, options, onBasicFilterChange]
  )

  const onShowDropdown = () => {
    if (null === filterRef.current) return
    onShowFilterDropdown(filterRef.current)
  }

  const onHideDropdown = () => {
    if (null === filterRef.current) return
    onHideFilterDropdown(filterRef.current)
  }

  const onRemoveClick = () => {
    if (Object.hasOwn(filter, 'derived')) {
      for (const v of filter.derived) {
        onVariableRemove(v)
      }
    } else onVariableRemove(filter.key)
  }

  const itemTemplate = option =>
    option.disabled && option.icon == undefined ? (
      <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
    ) : (
      <span className="p-item-label">
        {option.icon ? <i className={`p-item-icon icon-aytmfd-${option.icon}`} /> : null}
        <span dangerouslySetInnerHTML={{ __html: option.label }}></span>
      </span>
    )

  const buttonLabelTemplate = useCallback(
    event => {
      let label = filter.title

      if ('dropdown' == filter.type) {
        if (values != undefined && 0 < values.length) label = `${filter.title}: ${values[0]}`
      } else if (event != undefined && event.value != undefined) {
        if (
          (('multistatemultiselect' == filter.type && !event.value.some(item => !item.state)) ||
            'multiselect' == filter.type) &&
          options.length == event.value.length
        ) {
          label = `${event.placeholder}: All`
        } else if (0 < event.value.length) {
          label =
            1 == event.value.length && ('multiselect' == filter.type || event.value[0].state)
              ? `${event.placeholder}: ${
                  options.find(
                    o => o.value == ('multiselect' == filter.type ? event.value[0] : event.value[0][event.optionValue])
                  )[event.optionLabel]
                }`
              : `${event.placeholder}: Filtered to ${event.value.length}`
        }
      } else if (event != undefined) label = event.placeholder

      return (
        <>
          {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
          <span dangerouslySetInnerHTML={{ __html: label }}></span>
        </>
      )
    },
    [filter, values, options]
  )

  useEffect(() => {
    const _values = null !== filterValues ? JSON.parse(filterValues) : filterValues
    setValues(_values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues])
  useEffect(() => {
    setOptions(filterOptions)
  }, [filterOptions])

  useEffect(() => {
    dropdownsRef.current[filter.key] = filterRef

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[filter.key]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, filterRef, dropdownsRef.current]) // TODO

  const renderComponent = () => {
    switch (filter.type) {
      case 'dropdown': {
        return (
          <Dropdown
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-dropdown`}
            data-selected={filterSelected}
            className={`p-dropdown-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            itemTemplate={itemTemplate}
            valueTemplate={buttonLabelTemplate}
            filter={filter && Array.isArray(options) && options.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            filterInputAutoFocus={false}
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multiselect': {
        return (
          <MultiSelect
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-multiselect`}
            data-selected={filterSelected}
            className={`p-multiselect-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={options.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      case 'multistatemultiselect': {
        return (
          <MultiStateMultiSelect
            ref={filterRef}
            data-testid={`stat-filters-row-${filter.key}-multistatemultiselect`}
            data-selected={filterSelected}
            className={`p-multiselect-rounded${filter.draggable && !features.filters.disabled ? ' p-composite-right' : ''}`}
            placeholder={filter.title}
            value={values}
            options={options}
            optionValue="value"
            optionLabel="label"
            itemTemplate={itemTemplate}
            buttonLabelTemplate={buttonLabelTemplate}
            panelHeaderTemplate={MultiSelectHeader}
            panelFooterTemplate={MultiSelectFooter}
            filter={options.some(o => !o.disabled)}
            filterBy="search,label"
            filterPlaceholder="Search items"
            resetFilterOnHide
            fixedDropdownDir="bottom"
            disabled={filterDisabled || !options.length}
            onChange={filterDisabled || !options.length ? null : onFilterChange}
            onShow={filterDisabled || !options.length ? null : onShowDropdown}
            onHide={filterDisabled || !options.length ? null : onHideDropdown}
          />
        )
      }
      default: {
        return null
      }
    }
  }

  return (
    <div
      data-id={filter.key}
      data-testid={`stat-filters-row-${filter.key}`}
      data-type={filter.type}
      data-selected={filterSelected}
      data-disabled={filterDisabled}
      className={`${style['filter-row']} ${style[`offset-${filter.lvl || 0}`]}`}
    >
      {filterSelected && (
        <span
          data-testid={`stat-filters-row-${filter.key}-reset`}
          className={`${style['filter-reset']}${filterDisabled ? ' p-disabled' : ''}`}
          aria-label="Clear filter"
          {...pressable(filterDisabled ? null : () => onResetBasicFilter(filter))}
        >
          <i className="icon-aytmfd-x-v2" />
        </span>
      )}
      <div ref={dndListItemRef} className={style['filter-row-inner']}>
        {'basic' == mode ? (
          <div className="p-fluid p-col-12">{renderComponent()}</div>
        ) : (
          <div className={`${style['filter-button']} p-disabled`}>
            {filter.icon ? <i className={`icon-aytmfd-${filter.icon}`} /> : null}
            <span className={style['filter-button-text']} dangerouslySetInnerHTML={{ __html: filter.title }}></span>
          </div>
        )}
        {/* the mark stands outside the row, past the edge the group clips while it opens or closes */}
        {!removable || features.data_lab_upload.disabled || filterDisabled ? null : (
          <span
            data-testid={`stat-filters-row-${filter.key}-remove`}
            className={`${style['filter-remove']}${processing ? ' ' + style['filter-remove-disabled'] : ''}`}
            aria-label="Remove variable"
            {...pressable(onRemoveClick)}
          >
            <i className={processing ? 'icon-aytmfd-refresh-v2 aytmfd-spin' : 'icon-aytmfd-trash'} />
          </span>
        )}
      </div>
      {error && <div className={style['filter-error-message']}>There was some problem during deletion</div>}
    </div>
  )
}

FiltersRowCustomVariable.propTypes = {
  filter: PropTypes.object,
  removable: PropTypes.bool,
  filterValues: PropTypes.any,
  filterOptions: PropTypes.any,
  error: PropTypes.bool,
  processing: PropTypes.bool
}

FiltersRowCustomVariable.displayName = 'FiltersRowCustomVariable'

export default FiltersRowCustomVariable
